import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await api.get(`/billing/checkout/status/${sessionId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
        toast.success('Payment successful!');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Still processing, check again in a few seconds
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Card className="max-w-md w-full bg-slate-800 border-slate-700">
        <CardContent className="pt-6 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 mx-auto mb-4 text-blue-400 animate-spin" />
              <h2 className="text-xl font-semibold text-white mb-2">Processing Payment</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                {paymentDetails?.message || 'Your subscription is now active.'}
              </p>
              {paymentDetails?.amount && (
                <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-muted-foreground">Amount Paid</p>
                  <p className="text-2xl font-bold text-white">
                    ${paymentDetails.amount} {paymentDetails.currency?.toUpperCase()}
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <Link to="/dashboard">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Go to Dashboard
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/dashboard/billing">
                  <Button variant="outline" className="w-full border-slate-600 text-muted-foreground/70">
                    View Billing Details
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <XCircle className="h-12 w-12 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Session Expired</h2>
              <p className="text-muted-foreground mb-6">
                Your checkout session has expired. Please try again.
              </p>
              <Link to="/dashboard/billing">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Return to Billing
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="h-12 w-12 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h2>
              <p className="text-muted-foreground mb-6">
                We couldn't verify your payment. Please contact support if you were charged.
              </p>
              <Link to="/dashboard/billing">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Return to Billing
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
